import React from 'react';
import { useSelector } from 'react-redux';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Card, Row, Col } from 'antd';

const MonthlyVisitorsGraph = () => {
  // Fetching the visitor data for this month from Redux
  const dailyVisitors = useSelector(state => state.dashboard?.data?.thisMonthVisitors);

  // If the data is not yet available, show a loading state
  if (!dailyVisitors) {
    return (
      <Card title="Visitor Statistics This Month" bordered={false}>
        <p>Loading data...</p>
      </Card>
    );
  }

  // Map daily visitors into data format for LineChart
  const dailyData = Object.entries(dailyVisitors).map(([day, pin]) => ({
    day: `Day ${parseInt(day) + 1}`, // Convert day index to Day 1, Day 2, ...
    visitors: pin,
  }));

  // Group daily visitors into weeks for BarChart
  const weeks = Array(5).fill(0); // Assume up to 5 weeks in a month
  Object.entries(dailyVisitors).forEach(([day, pin]) => {
    const weekIndex = Math.floor(parseInt(day) / 7); // Calculate week index
    weeks[weekIndex] += pin; // Sum visitors for the week
  });

  const weeklyData = weeks.map((visitors, index) => ({
    week: `Week ${index + 1}`,
    visitors,
  }));

  // Fixed color map for each week
  const weekColorMap = {
    1: '#32cd32',  // Green for Week 1
    2: '#ff4d4d',  // Red for Week 2
    3: '#ffa500',  // Orange for Week 3
    4: '#1e90ff',  // Blue for Week 4
    5: '#8a2be2',  // Purple for Week 5
  };

  // Custom Tooltip for BarChart and LineChart
  const CustomTooltip = ({ payload, label }) => {
    if (payload && payload.length) {
      const data = payload[0];
      return (
        <div className="custom-tooltip">
          <p>{`${label}: ${data.value} Visitors`}</p>
        </div>
      );
    }
    return null;
  };

  // Calculate max visitors to scale the Y-Axis
  const maxVisitors = Math.max(...weeks);

  return (
    <div className='monthlygraphs'>
      <Row gutter={[16, 16]}>
        {/* Monthly Daily Visitors Graph */}
        <Col xs={24} md={12}>
          <Card title="Visitor Trends This Month (Daily)" bordered={false}>
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={dailyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="day" />
                <YAxis domain={[0, maxVisitors]} /> {/* Adjust Y-Axis based on max visitors */}
                <Tooltip content={<CustomTooltip />} />
                <Line type="monotone" dataKey="visitors" stroke="#8884d8" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        </Col>

        {/* Monthly Weekly Visitors Graph */}
        <Col xs={24} md={12}>
          <Card title="Visitor Statistics This Month (Weekly)" bordered={false}>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={weeklyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="week" />
                <YAxis domain={[0, maxVisitors]} /> {/* Adjust Y-Axis based on max visitors */}
                <Tooltip content={<CustomTooltip />} />
                {/* Removed Legend as it's not necessary */}
                {weeklyData.map((entry, index) => (
                  <Bar
                    key={entry.week}
                    dataKey="visitors"
                    fill={weekColorMap[index + 1]} 
                    barSize={30}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default MonthlyVisitorsGraph;

